import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "Diwali", images: ["diwali1.png", "diwali2.png", "diwali3.png", "diwali4.png"] },
    { name: "Holi", images: ["holi1.png", "holi2.png", "holi3.png", "holi4.png"] },
    { name: "Makar Sankranti", images: ["makarsankranti1.png", "makarsankranti2.png", "makarsankranti3.png", "makarsankranti4.png"] },
    { name: "Durga Puja", images: ["durgapuja1.png", "durgapuja2.png", "durgapuja3.png", "durgapuja4.png"] },
    { name: "Raksha Bandhan", images: ["rakshabandhan1.png", "rakshabandhan2.png", "rakshabandhan3.png", "rakshabandhan4.png"] },
    { name: "Ganesh Chaturthi", images: ["ganeshchaturthi1.png", "ganeshchaturthi2.png", "ganeshchaturthi3.png", "ganeshchaturthi4.png"] },
    { name: "Eid", images: ["eid1.png", "eid2.png", "eid3.png", "eid4.png"] },
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessFestival = () => {
    const getNewQuestion = () => {
        document.title = "Guess the Festival – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            nextQuestion()
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce nursery kids to cultural celebrations with the "Guess the Festival" activity on Mamas Guidance—an interactive game that helps children identify popular festivals through fun clues and visuals, promoting cultural awareness and learning.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Guess the Festival</h1>

                                    <p>
                                        Let your child explore the joy of celebrations with our fun-filled <strong>Guess the Festival</strong> worksheet! This activity introduces young learners to <strong>popular Indian festivals</strong> using picture clues, symbols, and simple questions.
                                    </p>

                                    <h2>🎉 What’s in the Worksheet?</h2>
                                    <p>
                                        Children will look at pictures or read short clues like <strong>“Which festival has firecrackers?”</strong>, <strong>“Where do we decorate a Christmas tree?”</strong> or <strong>“Which festival is celebrated with colors?”</strong>. They will guess from options like <b><em>Diwali, Holi, Christmas, Eid, Raksha Bandhan, and more</em></b>.
                                    </p>

                                    <h2>🎯 What Your Child Will Learn</h2>
                                    <ul>
                                        <li>Recognize major Indian festivals by their <strong>symbols and celebrations</strong></li>
                                        <li>Associate festival-related objects (<strong>lights, sweets, rakhis, colors, etc.</strong>)</li>
                                        <li>Build awareness of cultural traditions and unity</li>
                                        <li>Develop logical thinking and observation skills</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show the child each picture or read the question aloud.</li>
                                        <li>Ask them to observe carefully and think of what they know about the festival.</li>
                                        <li>Help them choose or circle the correct answer.</li>
                                        <li>Discuss how your family celebrates that festival to create real-life connections.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess the Area</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Pick Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Matters</h2>
                                    <p>
                                        Festivals teach children about joy, tradition, and community. This worksheet helps introduce these concepts <strong>early, nurturing curiosity, respect for diversity, and a love for learning about culture</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Guess the Festival</h1>

                                <p>
                                    Let your child explore the joy of celebrations with our fun-filled <strong>Guess the Festival</strong> worksheet! This activity introduces young learners to <strong>popular Indian festivals</strong> using picture clues, symbols, and simple questions.
                                </p>

                                <h2>🎉 What’s in the Worksheet?</h2>
                                <p>
                                    Children will look at pictures or read short clues like <strong>“Which festival has firecrackers?”</strong>, <strong>“Where do we decorate a Christmas tree?”</strong> or <strong>“Which festival is celebrated with colors?”</strong>. They will guess from options like <b><em>Diwali, Holi, Christmas, Eid, Raksha Bandhan, and more</em></b>.
                                </p>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Recognize major Indian festivals by their <strong>symbols and celebrations</strong></li>
                                    <li>Associate festival-related objects (<strong>lights, sweets, rakhis, colors, etc.</strong>)</li>
                                    <li>Build awareness of cultural traditions and unity</li>
                                    <li>Develop logical thinking and observation skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show the child each picture or read the question aloud.</li>
                                    <li>Ask them to observe carefully and think of what they know about the festival.</li>
                                    <li>Help them choose or circle the correct answer.</li>
                                    <li>Discuss how your family celebrates that festival to create real-life connections.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess the Area</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Pick Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Matters</h2>
                                <p>
                                    Festivals teach children about joy, tradition, and community. This worksheet helps introduce these concepts <strong>early, nurturing curiosity, respect for diversity, and a love for learning about culture</strong>.
                                </p>
                            </article>
                            <img src={process.env.REACT_APP_API_URL + image} className="img-thumbnail season-image helper-image" />
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_LOADING_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GuessFestival;
